import React from 'react'
import { Drawer, Descriptions, Divider, Space, Button, Tag } from 'antd'
import { TruckOutlined, CloseCircleOutlined } from '@ant-design/icons'
import type { Order } from '../types/order.types'
import { OrderTimeline } from './OrderTimeline'
import { formatINR, formatIST } from '../../../shared/utils/formatters'

interface OrderDetailsDrawerProps {
    order: Order | null
    open: boolean
    onClose: () => void
    onUpdateStatus: () => void
    onCancelOrder: () => void
    canCancel?: boolean
}

export const OrderDetailsDrawer: React.FC<OrderDetailsDrawerProps> = ({
    order,
    open,
    onClose,
    onUpdateStatus,
    onCancelOrder,
    canCancel = true
}) => {
    if (!order) return null

    const getStatusColor = (status: string) => {
        const colors = {
            pending: 'orange',
            confirmed: 'blue',
            shipped: 'cyan',
            delivered: 'green',
            cancelled: 'red',
        }
        return colors[status as keyof typeof colors] || 'default'
    }

    const getPaymentStatusColor = (status: string) => {
        const colors = {
            pending: 'orange',
            paid: 'green',
            refunded: 'gray',
        }
        return colors[status as keyof typeof colors] || 'default'
    }

    return (
        <Drawer
            title="Order Details"
            placement="right"
            width={700}
            onClose={onClose}
            open={open}
        >
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Descriptions title="Order Information" column={1} bordered>
                    <Descriptions.Item label="Order ID">
                        <strong>{order.id}</strong>
                    </Descriptions.Item>
                    <Descriptions.Item label="RFQ ID">{order.rfq_id}</Descriptions.Item>
                    <Descriptions.Item label="Customer">{order.customer_name}</Descriptions.Item>
                    <Descriptions.Item label="Merchant">{order.merchant_name}</Descriptions.Item>
                    <Descriptions.Item label="Product">
                        {order.items?.map(i => i.product_id).join(', ')}
                    </Descriptions.Item>
                    <Descriptions.Item label="Quantity">
                        {order.items?.reduce((acc, i) => acc + (Number(i.quantity) || 0), 0)} units
                    </Descriptions.Item>
                    <Descriptions.Item label="Amount">
                        <strong style={{ fontSize: 18, color: '#1890ff' }}>
                            {formatINR(order.total_amount)}
                        </strong>
                    </Descriptions.Item>
                    <Descriptions.Item label="Order Status">
                        <Tag color={getStatusColor(order.status)}>
                            {order.status.toUpperCase()}
                        </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Payment Status">
                        <Tag color={getPaymentStatusColor(order.payment_status || 'pending')}>
                            {(order.payment_status || 'pending').toUpperCase()}
                        </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Order Date">{formatIST(order.created_at)}</Descriptions.Item>
                    <Descriptions.Item label="Delivery Address">
                        {typeof order.delivery_address === 'object' && order.delivery_address !== null
                            ? `${(order.delivery_address as any).street || ''}, ${(order.delivery_address as any).city || ''}, ${(order.delivery_address as any).state || ''} - ${(order.delivery_address as any).pincode || ''}`.replace(/^[,\s]+|[,\s]+$/g, '')
                            : (order.delivery_address || 'N/A')}
                    </Descriptions.Item>
                    <Descriptions.Item label="Tracking Number">
                        {order.tracking_number || 'N/A'}
                    </Descriptions.Item>
                </Descriptions>

                <Divider />

                <div>
                    <h3>Order Timeline</h3>
                    <OrderTimeline order={order} />
                </div>

                <Divider />

                <Space>
                    <Button type="primary" icon={<TruckOutlined />} onClick={onUpdateStatus}>
                        Update Status
                    </Button>
                    {canCancel && order.status !== 'cancelled' && order.status !== 'delivered' && (
                        <Button danger icon={<CloseCircleOutlined />} onClick={onCancelOrder}>
                            Cancel Order
                        </Button>
                    )}
                </Space>
            </Space>
        </Drawer>
    )
}
